import React, { forwardRef, useState, useEffect, useRef } from 'react';
import { useTooltip } from '../../hooks';
import { keyCode, KEYBOARD_SHORTCUT } from '../../helpers';
import { Icon, Button, Label } from '../';
import { ValidationMessage } from '../ValidationMessage';
import { InlineSelect } from './components';
import './Input.css';

const _Input = (
  {
    name,
    type,
    placeholder,
    accept,
    label,
    labelAsPlaceholder,
    value,
    disabled = false,
    isNullable = false,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    autoComplete = 'off',
    required = false,
    className,
    inputClassName,
    icon,
    keyboardShortcut,
    iconAsButton = false,
    showCapsLockWarning = true,
    showTogglePasswordVisibility = true,
    validation,
    title,
    displayNameCode,
    labelPosition,
    withSelect = false,
    selectProps,
    preventEnterDefault = true,
    isOptional = false,
  },
  ref
) => {
  const { showTooltip, hideTooltip } = useTooltip();

  const [inputType, setInputType] = useState(type);
  const [isActive, setIsActive] = useState(false);
  const [expanded, setExpanded] = useState(value ? true : false);
  const [capsLock, setCapsLock] = useState(false);

  const tmpRef = useRef();
  const inputRef = ref || tmpRef;
  const inputSelectRef = useRef([]);
  const inputFileRef = useRef();

  useEffect(() => {
    if (!value || value === '') {
      setIsActive(false);
    } else {
      setIsActive(true);
    }
  }, [value]);

  useEffect(() => {
    if (disabled) {
      togglePasswordVisibility('password');
    }
  }, [disabled]);

  const clearInput = () => {
    onChange({ value: '', name: name });
    inputRef.current && inputRef.current.focus();
  };

  const handleExpand = el => {
    onFocus && onFocus(el);

    inputRef.current.focus();
    setExpanded(true);
  };
  const handleBlur = element => {
    if (
      iconAsButton &&
      value === '' &&
      inputSelectRef.current.findIndex(el => el === element.relatedTarget) < 0
    ) {
      setExpanded(false);
    }
    setCapsLock(false);
    onBlur && onBlur(element);
  };
  const handleKeyDown = keyEvent => {
    if (inputType === 'password' && showCapsLockWarning) {
      if (keyEvent.getModifierState('CapsLock')) {
        setCapsLock(true);
      } else {
        setCapsLock(false);
      }
    }

    if (preventEnterDefault && keyEvent.keyCode === keyCode.ENTER) {
      keyEvent.preventDefault();
    }

    onKeyDown && onKeyDown(keyEvent, value);
  };

  const handleClick = () => {
    inputFileRef.current.click();
  };

  const togglePasswordVisibility = state => {
    if (type === 'password' && showTogglePasswordVisibility) {
      setInputType(prev => {
        return state || (prev === 'password' ? 'text' : 'password');
      });
    }
  };

  const getFileName = value => {
    return value ? value.split(/\\|\//).slice(-1) : 'No file selected';
  };
  const labelElement = (
    <Label
      label={label}
      className={isActive ? 'active' : ''}
      htmlFor={name}
      displayNameCode={displayNameCode}
      title={title}
      disabled={disabled}
      isOptional={isOptional}
    />
  );

  return (
    <div
      className={
        'CustomInput' +
        (className ? ' ' + className : '') +
        (disabled ? ' disabled' : '') +
        (labelAsPlaceholder ? ' label-as-placeholder' : '') +
        (icon ? ' with-icon' : '') +
        (iconAsButton && !expanded ? ' collapsed' : '') +
        (validation && !validation.valid ? ' has-error' : '') +
        (labelPosition ? ' label-position-' + labelPosition : '') +
        (inputType === 'password' && showTogglePasswordVisibility ? '' : ' show-password') +
        (withSelect ? ' with-select' : '')
      }
    >
      {/*
      <Label
        label={label}
        className={isActive ? 'active' : ''}
        htmlFor={name}
        title={title}
        disabled={disabled}
      />
      */}

      {inputType === 'file' ? (
        <div className="browse-button">
          <Button value="Browse..." onClick={handleClick} />

          <div className={'browse-button-file-name' + (value ? '' : ' no-file')}>
            {getFileName(value)}
            <div className="button-clear-input" onClick={clearInput} tabIndex={1}>
              <div className="button-clear-input-icon"></div>
            </div>
          </div>

          <div className="input-file-wrapper">
            <input
              type="file"
              name={name}
              ref={inputFileRef}
              //value={value} // Do not set this for files, otherwise an "An attempt was made to use an object that is not, or is no longer, usable" error will be thrown when the value is set.
              onChange={onChange}
              accept={accept}
            />
          </div>
        </div>
      ) : (
        <>
          {labelPosition === 'left' && labelElement}
          <div className="input-container-wrapper">
            <div className="input-container" onClick={() => inputRef.current.focus()}>
              {labelPosition !== 'left' && labelElement}
              {labelPosition === 'left' && isOptional && !isActive && (
                <span
                  className="is-optional"
                  onMouseEnter={() => showTooltip({ title: 'This field is optional to fill in' })}
                  onMouseLeave={hideTooltip}
                >
                  optional
                </span>
              )}

              <div className="input-content" onClick={handleExpand}>
                {icon && (
                  <div className="input-icon">
                    <Icon name={icon} />
                  </div>
                )}
                <input
                  className={'input' + (inputClassName ? ' ' + inputClassName : '')}
                  type={inputType || type}
                  ref={inputRef}
                  name={name}
                  placeholder={labelAsPlaceholder ? '' : placeholder}
                  value={value}
                  onChange={onChange}
                  onKeyDown={handleKeyDown}
                  disabled={disabled}
                  autoComplete={autoComplete}
                  onBlur={handleBlur}
                  onFocus={handleExpand}
                  required={required || required === 'required' ? 'required' : false}
                />
                {((isNullable && value !== '') ||
                  validation ||
                  capsLock ||
                  withSelect ||
                  keyboardShortcut ||
                  type === 'password') &&
                  !disabled && (
                    <div className="button-container">
                      {keyboardShortcut && !value && (
                        <div className="keyboard-shortcut">
                          {KEYBOARD_SHORTCUT[keyboardShortcut]}
                        </div>
                      )}
                      {capsLock && showCapsLockWarning && (
                        <div
                          className="input-button caps-lock-warning"
                          onMouseEnter={() => showTooltip({ title: 'Caps Lock is ON!' })}
                          onMouseLeave={hideTooltip}
                        >
                          <Icon name="caps_lock" />
                        </div>
                      )}
                      {validation && (
                        <div
                          className={
                            'input-button validation-icon' +
                            (validation.valid === true
                              ? ' valid'
                              : !validation.valid
                                ? ' error'
                                : ' ' + validation.valid)
                          }
                          onMouseEnter={() =>
                            showTooltip({
                              title: validation.message,
                              bodyText: validation.tooltip,
                              tooltipBody: validation.tooltipBody,
                            })
                          }
                          onMouseLeave={hideTooltip}
                        ></div>
                      )}
                      {isNullable && value !== '' && (
                        <div
                          className="input-button button-clear-input"
                          onClick={clearInput}
                          tabIndex={1}
                        >
                          <div className="button-clear-input-icon"></div>
                        </div>
                      )}
                      {type === 'password' && showTogglePasswordVisibility && (
                        <div
                          className="input-button button-show-password"
                          onMouseEnter={() =>
                            showTooltip({
                              title: inputType === 'password' ? 'Show password' : 'Hide password',
                            })
                          }
                          onMouseLeave={hideTooltip}
                          onClick={() => togglePasswordVisibility()}
                        >
                          <Icon name="toggle_visibility" />
                        </div>
                      )}
                    </div>
                  )}
              </div>
            </div>
            {withSelect && (
              <div className="select-buttons">
                {selectProps.map((props, i) => (
                  <div
                    className="input-container"
                    key={i}
                    onClick={() => inputSelectRef.current[i].focus()}
                  >
                    {/*props.label && 
              <Label
            label={props.label}
            className={isActive ? 'active' : ''}
            htmlFor={name}
            title={props.tooltip}
            disabled={disabled}
            isOptional={props.isOptional}
          />  
          */}

                    <div className="input-content">
                      <InlineSelect
                        ref={el => (inputSelectRef.current[i] = el)}
                        name={props.name}
                        value={props.value}
                        label={props.label}
                        tooltip={props.tooltip}
                        options={props.options}
                        onChange={props.onChange}
                        isMulti={props.isMulti}
                        onBlur={handleBlur}
                        inputRef={inputRef}
                        disabled={disabled}
                        isOptional={props.isOptional}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
      <ValidationMessage validation={validation} />
    </div>
  );
};

export const Input = forwardRef(_Input);
